import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material'
import { QuickBooksSyncUploadDto, quickBooksSyncUploadValidationSchema } from 'dtos'
import { Formik } from 'formik'

interface QuickBooksSyncUploadDialogProps {
  initialValues: QuickBooksSyncUploadDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: QuickBooksSyncUploadDto) => void
  open: boolean
}

export default function QuickBooksSyncUploadDialog({
  initialValues,
  isLoading,
  onClose,
  onSave,
  open
}: QuickBooksSyncUploadDialogProps) {
  return (
    <Dialog open={open} onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur
        validateOnChange
        validationSchema={quickBooksSyncUploadValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle>Upload QuickBooks Sync</DialogTitle>

            <DialogContent>
              <Grid container spacing={2} alignItems='center' sx={{ mt: 0 }}>
                <Grid item xs={12}>
                  <input
                    accept='.zip'
                    hidden
                    id='quickBooksSyncDocumentFileInput'
                    onChange={e => {
                      if (e.target.files && e.target.files.length > 0) {
                        setFieldValue('documentFile', e.target.files[0])
                      }
                    }}
                    type='file'
                  />

                  <Button
                    disabled={isLoading || isSubmitting}
                    onClick={() => document.getElementById('quickBooksSyncDocumentFileInput')?.click()}
                    variant='contained'
                  >
                    UPLOAD FILE
                  </Button>

                  <Typography color='#808080' display='inline' variant='body2' sx={{ ml: 1 }}>
                    {values.documentFile?.name ?? 'No file uploaded'}
                  </Typography>

                  {touched.documentFile && errors.documentFile && (
                    <Typography color='error' variant='body2'>
                      {errors.documentFile}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading || isSubmitting}
                    error={Boolean(touched.quickBooksVersion && errors.quickBooksVersion)}
                    fullWidth
                    helperText={touched.quickBooksVersion && errors.quickBooksVersion}
                    inputProps={{ maxLength: 255 }}
                    label='QuickBooks Version'
                    name='quickBooksVersion'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.quickBooksVersion ?? ''}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading || isSubmitting}
                    error={Boolean(touched.notes && errors.notes)}
                    fullWidth
                    helperText={touched.notes && errors.notes}
                    inputProps={{ maxLength: 50 }}
                    label='Notes'
                    name='notes'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.notes ?? ''}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={isLoading || isSubmitting} variant='text' color='inherit' onClick={onClose}>
                CANCEL
              </Button>

              <LoadingButton loading={isLoading || isSubmitting} type='submit' variant='contained'>
                SAVE
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  )
}
